<template>
  <div class="fmg-section-editor">
    <!-- TITLE -->
    <h5 class="mb-3">{{ $t('userAdmin.moveSections') }}</h5>

    <!-- ===================== -->
    <!-- REMOVE SECTION PANEL -->
    <!-- ===================== -->
    <div class="row mb-3">
      <div class="col-12 col-md-6">
        <div class="cap-label">{{ $t('userAdmin.removeSections') }}</div>

        <SingleSelectDropdown
          v-model="removeSelection"
          :items="removeDropdownItems"
          :searchable="true"
          :placeholder="$t('customerForm.pleaseChoose')"
          :allowempty="false"
          @change="handleRemoveSelected"
        />
      </div>
    </div>

    <!-- SELECTED SECTIONS TO REMOVE -->
    <div class="row selected-list" v-if="sectionsToRemove.length">
      <SelectedSectionItem
        v-for="sec in sectionsToRemove"
        :key="'rem_' + sec.id"
        :section="sec"
        @remove="undoRemove(sec.id)"
      />
    </div>

    <!-- SPACER -->
    <hr class="mt-4 mb-4" />

    <!-- ===================== -->
    <!-- ADD SECTION PANEL -->
    <!-- ===================== -->
    <div class="row mb-3">
      <div class="col-12 col-md-6">
        <div class="cap-label">{{ $t('userAdmin.addSections') }}</div>

        <SingleSelectDropdown
          v-model="addSelection"
          :items="addDropdownItems"
          :searchable="true"
          :placeholder="$t('customerForm.pleaseChoose')"
          :allowempty="false"
          @change="handleAddSelected"
        />
      </div>
    </div>

    <!-- SELECTED SECTIONS TO ADD -->
    <div class="row selected-list" v-if="sectionsToAdd.length">
      <SelectedSectionItem
        v-for="sec in sectionsToAdd"
        :key="'add_' + sec.id"
        :section="sec"
        @remove="undoAdd(sec.id)"
      />
    </div>

    <!-- SPACER -->
    <hr class="mt-4 mb-4" />

    <!-- ===================== -->
    <!-- RISKY FRIENDSHIPS PANEL -->
    <!-- ===================== -->
    <div v-if="calculatedRiskyFriendships.length > 0">
      <h5 class="mt-3">{{ $t('userAdmin.resultingRisks') }}</h5>

      <div v-if="calculatedRiskyFriendships.length === 0">
        <p>{{ $t('userAdmin.noRiskyFriendshipsAfterChanges') }}</p>
      </div>

      <div v-else class="risky-table mt-2">
        <table class="table table-sm table-bordered">
          <thead>
            <tr>
              <th>{{ $t('userAdmin.customer') }}</th>
              <th>{{ $t('userAdmin.volunteer') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="rf in calculatedRiskyFriendships"
              :key="'rf_' + rf.friendshipId"
            >
              <!-- Customer -->
              <td>
                <div class="fmg-person">
                  <span class="fmg-name">
                    {{ rf.customerFirstName }} {{ rf.customerLastName }}
                    (
                    <small :class="{ 'fmg-zip-move': rf.customerWillMove }">
                      {{ formatZip(rf.customerZipCode) }}
                    </small>
                    )
                  </span>

                  <font-awesome-icon
                    v-if="rf.customerWillMove"
                    icon="exclamation-triangle"
                    class="fmg-warning-icon"
                  />
                </div>
              </td>

              <!-- Volunteer -->
              <td>
                <div class="fmg-person">
                  <span class="fmg-name">
                    {{ rf.volunteerFirstName }} {{ rf.volunteerLastName }}
                    (
                    <small :class="{ 'fmg-zip-move': rf.volunteerWillMove }">
                      {{ formatZip(rf.volunteerZipCode) }}
                    </small>
                    )
                  </span>

                  <font-awesome-icon
                    v-if="rf.volunteerWillMove"
                    icon="exclamation-triangle"
                    class="fmg-warning-icon"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ===================== -->
    <!-- ACTION BUTTONS -->
    <!-- ===================== -->
    <div class="row mt-4 d-flex justify-content-between align-items-center">
      <!-- LEFT: Save -->
      <div>
        <sprbutton
          type="primary"
          :size="4"
          :title="$t('userAdmin.save')"
          class="spr-redborder button-width"
          :disabled="hasRisks"
          click-event="save"
          @save="emitSave"
        />
      </div>

      <!-- RIGHT: Delete -->
      <div>
        <sprbutton
          type="danger"
          :size="4"
          :title="$t('userAdmin.deleteGroup')"
          class="spr-redborder button-width"
          :disabled="!canDeleteFmg"
          click-event="delete"
          @delete="emitDelete"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import SingleSelectDropdown from '../common/Dropdown/SingleSelectDropdown.vue'
import SelectedSectionItem from '../useradmin/SelectedSectionItem.vue'
import sprbutton from '../SPRButton.vue'
import { SectionSimpleDto, RiskyFriendshipDto } from '@/types'

export default Vue.extend({
  name: 'FriendManagementGroupSectionEditor',

  components: {
    SingleSelectDropdown,
    SelectedSectionItem,
    sprbutton
  },

  props: {
    groupId: {
      type: Number,
      required: true
    },
    activeSections: {
      type: Array as () => SectionSimpleDto[],
      required: true
    },
    poolSections: {
      type: Array as () => SectionSimpleDto[],
      required: true
    },
    initialRiskyFriendships: {
      type: Array as () => RiskyFriendshipDto[],
      required: true
    }
  },

  data() {
    return {
      sectionsToRemove: [] as SectionSimpleDto[],
      sectionsToAdd: [] as SectionSimpleDto[],

      removeSelection: null as any,
      addSelection: null as any,

      calculatedRiskyFriendships: [] as RiskyFriendshipDto[],

      saving: false
    }
  },

  computed: {
    // Dropdown items for active → removable sections
    removeDropdownItems(): Array<{ id: number; text: string }> {
      return this.activeSections
        .filter((s) => !this.sectionsToRemove.some((r) => r.id === s.id))
        .map((s) => ({ id: s.id, text: s.name }))
    },

    // Dropdown items for pool → addable sections
    addDropdownItems(): Array<{ id: number; text: string }> {
      return this.poolSections
        .filter((s) => !this.sectionsToAdd.some((r) => r.id === s.id))
        .map((s) => ({ id: s.id, text: s.name }))
    },
    hasRisks(): boolean {
      return this.calculatedRiskyFriendships.length > 0
    },
    canDeleteFmg(): boolean {
      return this.activeSections.length === 0
    }
  },

  methods: {
    //
    // REMOVE SECTION SELECTED
    //
    handleRemoveSelected(selected: { id: number; text: string }) {
      if (!selected) return

      const sec = this.activeSections.find((s) => s.id === selected.id)
      if (!sec) return

      this.sectionsToRemove.push(sec)
      this.removeSelection = null

      this.emitImpact()
    },

    formatZip(zip: number | string | null | undefined): string {
      if (zip === null || zip === undefined) return '-'
      return zip.toString().padStart(5, '0')
    },

    undoRemove(id: number) {
      this.sectionsToRemove = this.sectionsToRemove.filter((s) => s.id !== id)
      this.emitImpact()
    },

    //
    // ADD SECTION SELECTED
    //
    handleAddSelected(selected: { id: number; text: string }) {
      if (!selected) return

      const sec = this.poolSections.find((s) => s.id === selected.id)
      if (!sec) return

      this.sectionsToAdd.push(sec)
      this.addSelection = null

      this.emitImpact()
    },

    undoAdd(id: number) {
      this.sectionsToAdd = this.sectionsToAdd.filter((s) => s.id !== id)
      this.emitImpact()
    },

    //
    // ASK PARENT TO CALCULATE RISK IMPACT
    //
    async emitImpact() {
      if (
        this.sectionsToRemove.length === 0 &&
        this.sectionsToAdd.length === 0
      ) {
        this.calculatedRiskyFriendships = []
        return
      }
      this.$emit(
        'calculate-impact',
        this.groupId,
        this.sectionsToRemove.map((s) => s.id),
        this.sectionsToAdd.map((s) => s.id),
        (updatedRisks: RiskyFriendshipDto[]) => {
          this.calculatedRiskyFriendships = updatedRisks
        }
      )
    },

    //
    // SAVE BUTTON
    //
    emitSave() {
      this.$emit(
        'save-sections',
        this.groupId,
        this.sectionsToRemove.map((s) => s.id),
        this.sectionsToAdd.map((s) => s.id)
      )
      this.sectionsToRemove = []
      this.sectionsToAdd = []
      this.calculatedRiskyFriendships = []
    },
    emitDelete() {
      this.$emit('delete-fmg', this.groupId)
    }
  }
})
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/_variables.scss';

.fmg-section-editor {
  background: $brand-grey2;
  padding: 20px;
  border-radius: 4px;
  margin-top: 10px;

  .cap-label {
    font-family: 'Signa-Light', Sans-Serif;
    color: $brand-red;
    font-weight: bold;
    margin-bottom: 6px;
  }

  .selected-list {
    display: flex;
    flex-wrap: wrap;
  }

  .risky-table {
    max-height: 260px;
    overflow-y: auto;
  }
  .fmg-warning-icon {
    margin-left: 6px;
    font-size: 14px;
    color: $brand-red; // but shape still recognizable without color
  }

  .fmg-zip-move {
    font-weight: bold;
    color: $brand-red;
  }

  .fmg-person {
    display: flex;
    align-items: center;
    gap: 6px; // space between name and icon
  }

  .fmg-name {
    font-weight: 600;
  }
}
</style>
